import { defaultPropertyDefinitions } from "@buildwithflux/constants";
import { nestedNodeDelimiter, } from "@buildwithflux/models";
import { clone, cloneDeep, get, pickBy, update } from "lodash";
import { devAssertVar } from "../../helpers";
import { getPropertyValueRobust } from "../../property";
import { ElementHelper } from "../ElementHelper";
function parseDesignator(designator) {
    const match = designator.match(/([A-Z]+)([0-9]+)/);
    if (!match)
        return;
    const [prefix, numericStr] = match.slice(1);
    if (!prefix || !numericStr)
        return;
    try {
        const number = Number.parseInt(numericStr);
        return { prefix, number };
    }
    catch (e) {
        // eslint-disable-next-line no-console
        console.warn(`Could not parse designator as prefix and number: ${designator}`);
        return;
    }
}
class DesignatorNamespace {
    // TODO: consider better data structure for performance
    parsed = new Map();
    unparseable = new Set();
    add(designator) {
        const parsed = parseDesignator(designator);
        if (!parsed) {
            this.unparseable.add(designator);
            return;
        }
        const { prefix, number } = parsed;
        const numbersForPrefix = this.parsed.get(prefix) ?? new Set();
        numbersForPrefix.add(number);
        this.parsed.set(prefix, numbersForPrefix);
    }
    has(designator) {
        const parsed = parseDesignator(designator);
        if (!parsed) {
            return this.unparseable.has(designator);
        }
        const { prefix, number } = parsed;
        const numbersForPrefix = this.parsed.get(prefix) ?? new Set();
        return numbersForPrefix.has(number);
    }
    getNextDesignator(prefix) {
        const alreadyTakenNumbers = this.parsed.get(prefix) ?? new Set();
        let i = 1;
        while (alreadyTakenNumbers.has(i)) {
            i += 1;
        }
        return `${prefix}${i}`;
    }
}
/**
 * Creates a namespace containing all the designator prefixes and numbers taken for each prefix
 * within the provided designators.
 *
 * For example: ['R1', 'R2', 'D1', 'ignored'] becomes {"R" => {1, 2}, "D" => {1}}
 */
function createDesignatorNamespaceFrom(designators) {
    const namespace = new DesignatorNamespace();
    for (const designator of designators) {
        namespace.add(designator);
    }
    return namespace;
}
export class DesignatorMapping {
    mapping;
    constructor(fromData) {
        this.mapping = fromData ?? {};
    }
    static merge(a, b) {
        const merged = new DesignatorMapping(a.toData());
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        b.forEach(({ uid, from, to }) => {
            merged.set(uid, { from, to });
        });
        return merged;
    }
    toData() {
        return cloneDeep(this.mapping);
    }
    set(uid, map) {
        this.mapping[uid] = clone(map);
    }
    forUid(uid) {
        return this.mapping[uid];
    }
    forEach(cb) {
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.entries(this.mapping).forEach(([uid, { from, to }]) => cb({ uid, from, to }));
    }
}
/**
 * Map from designators within a part to designators within a project, avoiding
 * duplicates when necessary.
 */
export function remapDesignators(params) {
    const reservedNamespace = createDesignatorNamespaceFrom(params.reserved);
    const mapping = new DesignatorMapping();
    const noConflicts = [];
    const toRename = [];
    for (const renameable of params.renameables) {
        const currentDesignator = renameable.designator;
        if (!currentDesignator) {
            // I.e. a part has a designator prefix, but empty designator value
            toRename.push(renameable);
            continue;
        }
        const conflictInHostNamespace = reservedNamespace.has(currentDesignator);
        if (conflictInHostNamespace) {
            toRename.push(renameable);
            continue;
        }
        const conflictWithOtherRenameable = params.renameables.some(({ uid, designator }) => renameable.uid !== uid && designator !== undefined && renameable.designator === designator);
        if (conflictWithOtherRenameable) {
            toRename.push(renameable);
            continue;
        }
        noConflicts.push({ uid: renameable.uid, prefix: renameable.prefix, designator: currentDesignator });
    }
    for (const part of noConflicts) {
        if (!part.designator)
            continue;
        mapping.set(part.uid, { from: part.designator, to: part.designator });
        reservedNamespace.add(part.designator);
    }
    for (const part of toRename) {
        const newDesignator = reservedNamespace.getNextDesignator(part.prefix);
        mapping.set(part.uid, { from: part.designator, to: newDesignator });
        reservedNamespace.add(newDesignator);
    }
    return mapping;
}
export function getAllBomExportableDesignatorsFrom(elements) {
    const bomExportableElememtsInHostDoc = pickBy(ElementHelper.flattenElements(elements), ElementHelper.isValidBomElement);
    const designators = Object.values(bomExportableElememtsInHostDoc)
        .map((element) => element.label)
        .filter((p) => !!p);
    return designators;
}
/**
 * Return the sub elements whose designators can be renamed.
 *
 * @remarks Not all sub elements with designators can be remapped. For example, if a sub element has
 * a designator, but no "Designator Prefix" property set, it is unclear how to generate a new designator
 * for that part.
 * Also, only parts that appear on the BoM can be renamed.
 */
function getRenameablePartsIn(element) {
    const bomExportableElementsInSubDoc = pickBy(ElementHelper.flattenElements(element.part_version_data_cache.extracted_element_cache ?? {}), ElementHelper.isValidBomElement);
    const renameablesInSubDoc = Object.entries(bomExportableElementsInSubDoc)
        .map(([uid, el]) => {
        if (!el.properties) {
            return undefined;
        }
        /**
         * The following property-lookup code is basically a generalization of getPropertyValue to handle "old"
         * property data.
         */
        const prefixValue = getPropertyValueRobust(defaultPropertyDefinitions.designator_prefix, el.properties);
        if (!prefixValue) {
            return undefined;
        }
        const renameable = {
            uid,
            prefix: prefixValue.toString(),
            designator: el.label,
        };
        return renameable;
    })
        .filter((p) => !!p);
    return renameablesInSubDoc;
}
function applyMappingToExtractedElementCache(newElement, mapping) {
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    mapping.forEach(({ uid, to: newDesignator }) => {
        const nestedUids = uid.split(nestedNodeDelimiter);
        // Construct nested path of part version data
        // e.g. part_version_data_cache.extracted_element_cache[<parent>]
        //       .part_version_data_cache.extracted_element_cache[<child>]
        //        ... etc
        const fullPath = nestedUids.flatMap((nestedUid) => [
            "part_version_data_cache",
            "extracted_element_cache",
            nestedUid,
        ]);
        // Check that it exists
        const subPart = get(newElement, fullPath);
        if (!subPart)
            return; // This should never happen.
        // Update the designator
        // NOTE: not ideal to change part version data, but the alternative of updating
        // designator values downstream requires changing a lot of code. This is the simplest
        // solution for now.
        update(newElement, fullPath, (prev) => {
            const prevEl = prev;
            return { ...prevEl, label: newDesignator };
        });
    });
}
function applyMappingToPcbNodes(newElement, mapping) {
    const pcbNodes = newElement.part_version_data_cache.pcbLayoutNodes;
    if (!pcbNodes)
        return; // QUESTION: when would this not be defined?
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    mapping.forEach(({ uid, to: newDesignator }) => {
        const node = pcbNodes[uid];
        if (!node)
            return; // This should never happen
        node.name = newDesignator;
    });
}
function storeNewMappingOnElement(params) {
    const previousMapping = params.element.designatorMappings
        ? new DesignatorMapping(params.element.designatorMappings)
        : undefined;
    if (!previousMapping) {
        // The new mapping is the first mapping ever applied
        params.element.designatorMappings = params.newMapping.toData();
        return;
    }
    // Otherwise, keep the original "from" designators
    const mappingWithOriginalDesignatorsIntact = new DesignatorMapping(previousMapping.toData()); // Start with previous map
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    params.newMapping.forEach((newMap) => {
        const previousMap = previousMapping.forUid(newMap.uid);
        mappingWithOriginalDesignatorsIntact.set(newMap.uid, {
            // Only update "to" value
            from: previousMap ? previousMap.from : newMap.from,
            to: newMap.to,
        });
    });
    params.element.designatorMappings = mappingWithOriginalDesignatorsIntact.toData();
}
/**
 * Updates element data in place.
 */
export function applyDesignatorMappingToElement(params) {
    // Map the element cache (updates designators on the pcb silk layer)
    applyMappingToExtractedElementCache(params.element, params.mapping);
    // Map the pcb nodes (without doing this, the silkscreen designator will not match the
    // name shown in the pcb object tree / inspector)
    applyMappingToPcbNodes(params.element, params.mapping);
    // Save the mapping in order to resolve selectors that referred to the old designators.
    // See idSelectorMatchesObjectData
    storeNewMappingOnElement({ element: params.element, newMapping: params.mapping });
}
/**
 * Mutates the element. Renames BOM-exportable element designators within a subdocument to avoid conflicts in existing project.
 *
 * @remarks The element itself may have duplicate designators within it (e.g. an old part that was created before
 * designator remapping was supported), in which case these conflicts will also be fixed.
 */
export const remapDesignatorsToAvoidDuplicates = (params) => {
    const renameablesInSubDoc = getRenameablePartsIn(params.elementToRemap);
    if (renameablesInSubDoc.length === 0) {
        // No mapping to apply, since no elements can be renamed
        return;
    }
    // Of all the parts that can be renamed, we should avoid renaming parts that were already remapped
    // during a previous import. We want things to be stable as much as possible on part version update.
    // In order to achieve this, we first remap any previously-mapped parts that conflict with the host
    // project. Then, we do a second pass to rename any of the *new* parts that need remapping.
    const previousMapping = params.elementToRemap.designatorMappings
        ? new DesignatorMapping(params.elementToRemap.designatorMappings)
        : undefined;
    const previouslyMappedParts = [];
    const newParts = [];
    for (const part of renameablesInSubDoc) {
        if (previousMapping && !!previousMapping.forUid(part.uid)) {
            previouslyMappedParts.push(part);
        }
        else {
            newParts.push(part);
        }
    }
    const mappingForPreviousParts = remapDesignators({
        renameables: previouslyMappedParts,
        reserved: params.designatorsToAvoid, // Use project namespace
    });
    const newDesignatorsOfPreviouslyMappedParts = [];
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    mappingForPreviousParts.forEach(({ to }) => newDesignatorsOfPreviouslyMappedParts.push(to));
    const mappingForNewParts = remapDesignators({
        renameables: newParts,
        reserved: params.designatorsToAvoid.concat(newDesignatorsOfPreviouslyMappedParts), // Use project namespace and all the designators we just mapped
    });
    // Now merge the two mappings to get the final mapping for the module
    const mapping = DesignatorMapping.merge(mappingForNewParts, mappingForPreviousParts);
    applyDesignatorMappingToElement({ element: params.elementToRemap, mapping });
};
export function getAllSubPathsInHierarchicalOrder(fromSubdocUid) {
    const subDocUidParts = fromSubdocUid.split(nestedNodeDelimiter);
    return subDocUidParts.reduce((uids, v) => {
        if (uids.length === 0)
            return [v];
        const lastUid = uids[uids.length - 1];
        const next = [lastUid, v].join(nestedNodeDelimiter);
        return uids.concat(next);
    }, []);
}
/**
 * Designator remapping can change an element's designator depending on where it
 * is used. This functions returns the designator as it appears within the context
 * provided (e.g. host document, or within a subdoc).
 */
export function getDesignatorWithinContext(params) {
    // Strategy: If we want the designator from a certain context, we need to
    // reverse the designator mappings applied since that context.
    // For example, in the case of nested docs A > B > C, where the designator is
    // remapped when C is imported to B and B to A, we need to undo the mapping
    // of A, then B, in order to get back to what it was in C.
    const allSubPaths = getAllSubPathsInHierarchicalOrder(params.fromSubdocUid);
    // A basic assumption here is that the node is actually under the subdoc,
    // otherwise this whole thing fails (see nodeUidFromThisContext)
    const nodeIsUnderSubdoc = params.nodeUid.startsWith(params.fromSubdocUid);
    if (!devAssertVar(nodeIsUnderSubdoc, params.logger))
        return;
    // Starting at the top, apply reverse mappings from each ancestor to get
    // the designator as seen within the subdoc
    let designatorInContext = params.designatorInHostDoc;
    for (const nestedUid of allSubPaths) {
        const element = ElementHelper.getNestedElementData(params.allElements, nestedUid);
        if (!devAssertVar(element, params.logger))
            return;
        const mappingData = element.designatorMappings;
        // No mapping, skip
        // NOTE: this is expected in legacy documents
        if (!mappingData)
            continue;
        // Remove the prefix scope of the node's uid
        const nodeUidFromThisContext = params.nodeUid.slice(nestedUid.length + nestedNodeDelimiter.length); // remove first part
        const mapping = new DesignatorMapping(mappingData);
        const mapped = mapping.forUid(nodeUidFromThisContext);
        // Skip if no mapping was made at this level.
        // This happens when there were no duplicate conflicts that
        // would require renaming a designator.
        if (!mapped)
            continue;
        if (mapped.from === undefined) {
            // If the value before mapping was undefined (e.g. it had no designator in this context, but was
            // then remapped using the designator prefix property), then there's nothing to match from this context
            return undefined;
        }
        // Save the latest value from the lowest context
        designatorInContext = mapped.from;
    }
    return designatorInContext;
}
//# sourceMappingURL=remapDesignators.js.map